/**
 * ============================================================================
 *  PROCESSO SELETIVO — CONFIGURAÇÃO E PERGUNTAS
 * ============================================================================
 *
 *  Este arquivo é o ÚNICO lugar que você precisa editar para mudar o
 *  formulário do PS. A página /inscricao lê tudo daqui automaticamente.
 *
 *  ----------------------------------------------------------------------
 *  COMO ADICIONAR UMA PERGUNTA
 *  ----------------------------------------------------------------------
 *  Toda pergunta é um objeto dentro do array `questions` de uma seção:
 *
 *  {
 *    id: 'nome_completo',      // OBRIGATÓRIO. Chave única — vira a coluna
 *                              // no banco (Supabase). Use snake_case, sem
 *                              // acento e sem espaço.
 *    label: 'Nome completo',   // OBRIGATÓRIO. A pergunta que aparece na tela.
 *    type: 'text',             // OBRIGATÓRIO. Ver tabela de tipos abaixo.
 *    required: true,           // true = obrigatória / false = opcional.
 *    placeholder: '...',       // Opcional. Texto de exemplo dentro do campo.
 *    help: '...',              // Opcional. Textinho de ajuda abaixo do campo.
 *    maxLength: 600,           // Opcional. Só para 'text' e 'textarea'.
 *                              // Mostra contador de caracteres.
 *    options: [...],           // Só para 'radio', 'select' e 'checkbox'.
 *    columns: 2,               // Opcional. Só para 'checkbox': nº de colunas.
 *    width: 'full',            // Opcional: 'full' (padrão) ou 'half'.
 *                              // 'half' coloca dois campos lado a lado.
 *    showIf: {...},            // Opcional. Pergunta condicional — ver abaixo.
 *  }
 *
 *  ----------------------------------------------------------------------
 *  TIPOS DISPONÍVEIS (campo `type`)
 *  ----------------------------------------------------------------------
 *  'text'      → Resposta aberta curta (uma linha).
 *  'textarea'  → Resposta aberta longa (várias linhas / dissertativa).
 *  'email'     → Uma linha, valida se é um e-mail de verdade.
 *  'tel'       → Uma linha, valida telefone e formata (11) 91234-5678.
 *  'number'    → Só números. Aceita `min` e `max`.
 *  'url'       → Link único. Valida a URL e aceita digitar sem "https://".
 *  'radio'     → Marcar UMA opção, exibida como lista de botões.
 *  'select'    → Marcar UMA opção, exibida como menu suspenso (dropdown).
 *                Use quando houver muitas opções (mais de ~6).
 *  'checkbox'  → Marcar VÁRIAS opções. A resposta é salva como lista.
 *  'consent'   → Uma única caixa de confirmação ("Declaro que...").
 *                Com required: true, só envia se estiver marcada.
 *
 *  ----------------------------------------------------------------------
 *  COMO ESCREVER AS OPÇÕES ('radio', 'select' e 'checkbox')
 *  ----------------------------------------------------------------------
 *  Forma simples — o texto é salvo exatamente como está escrito:
 *      options: ['Sim', 'Não', 'Talvez']
 *
 *  Forma completa — separa o que aparece na tela do que é salvo no banco:
 *      options: [
 *        { value: 'sim',  label: 'Sim, tenho disponibilidade' },
 *        { value: 'nao',  label: 'Não tenho disponibilidade' },
 *      ]
 *
 *  ----------------------------------------------------------------------
 *  PERGUNTAS E SEÇÕES CONDICIONAIS (`showIf`)
 *  ----------------------------------------------------------------------
 *  Uma pergunta (ou uma seção inteira) só aparece se a condição for
 *  verdadeira. Três formatos:
 *
 *      showIf: { question: 'participa_entidade', equals: 'sim' }
 *      showIf: { question: 'trilha', in: ['tech', 'ambas'] }
 *      showIf: { question: 'tecnologias', answered: true }  // marcou ao menos uma
 *
 *  Quem está escondido não é validado e é salvo como vazio no banco.
 *
 *  ----------------------------------------------------------------------
 *  SEÇÕES
 *  ----------------------------------------------------------------------
 *  As perguntas são agrupadas em seções (`sections`). Cada seção vira um
 *  passo do formulário, com barra de progresso. Campos de uma seção:
 *
 *      id, title, description  → cabeçalho do passo
 *      intro: ['parágrafo 1', 'parágrafo 2']  → texto de transição (opcional)
 *      showIf: {...}           → mostra a seção só em certas respostas
 *      questions: [...]        → as perguntas
 *
 * ============================================================================
 */

/* ---------------------------------------------------------------------------
 *  CONFIGURAÇÕES GERAIS DO PROCESSO SELETIVO
 * ------------------------------------------------------------------------- */
export const psConfig = {
  /* =========================================================================
   *  ⚡ INTERRUPTOR DO PROCESSO SELETIVO
   * =========================================================================
   *  Vire para `false` quando o PS acabar. Com isso, de uma vez só:
   *    • a seção de inscrição some da home;
   *    • o botão verde do menu volta a ser "Seja Parceiro";
   *    • a página /inscricao passa a redirecionar para a home.
   *  Nada mais precisa ser mexido. Para apagar de vez, veja o roteiro em
   *  COMO-REMOVER-O-PS.md, na raiz do projeto.
   * ======================================================================= */
  ativo: false,

  // Textos do topo da página
  eyebrow: 'Processo Seletivo 2026',
  title: 'Faça parte da',
  titleHighlight: 'AgroTech Inteli',
  subtitle:
    'Ficamos muito felizes com o seu interesse em fazer parte da Liga AgroTech. Este formulário tem como objetivo conhecer melhor você, sua trajetória, seus interesses e sua disponibilidade — para que possamos entender como a Liga pode contribuir com o seu desenvolvimento.',

  // Aviso em destaque logo abaixo do subtítulo
  notice:
    'Este formulário não possui caráter eliminatório. Não existe resposta certa ou errada — queremos apenas conhecer melhor cada inscrito.',

  // Prazo exibido no selo do topo
  deadline: '18 de agosto de 2026',

  /* Mesma data em formato de calendário — usada pela contagem regressiva da
   * home. Mantenha as duas em sincronia. O -03:00 é o fuso de Brasília. */
  deadlineDate: '2026-08-18T23:59:59-03:00',

  /* -------------------------------------------------------------------------
   *  SEÇÃO DE INSCRIÇÃO NA HOME (aparece acima do Hero enquanto `ativo`)
   * ---------------------------------------------------------------------- */
  homeCta: {
    eyebrow: 'Inscrições abertas · Processo Seletivo 2026',
    title: 'Sua vaga na',
    titleHighlight: 'AgroTech Inteli',
    description:
      'Estamos abrindo as portas da primeira liga universitária de agro e tecnologia do Brasil. Duas trilhas, projetos reais com empresas do setor e um time que constrói tecnologia de código aberto para o campo.',
    buttonLabel: 'Quero me inscrever',
    secondaryLabel: 'Conhecer a liga',
    trilhas: [
      {
        nome: 'Trilha Tech',
        descricao: 'Desenvolvimento dos projetos que entregamos para as empresas parceiras.',
      },
      {
        nome: 'Trilha Trainees',
        descricao: 'Negócios, marketing, parcerias e a gestão que sustenta a liga.',
      },
    ],
  },

  /* Materiais para leitura antes de responder.
   * Salve os PDFs em public/media/ com exatamente estes nomes de arquivo. */
  manuais: {
    title: 'Leia antes de responder',
    description:
      'Os manuais explicam como funciona cada trilha da Liga. A leitura ajuda bastante na hora de escolher a sua.',
    items: [
      { label: 'Manual da Trilha Tech', href: '/media/manual-trilha-tech.pdf' },
      { label: 'Manual da Trilha Trainees', href: '/media/manual-trilha-trainees.pdf' },
    ],
  },

  // Cronograma do processo. Para esconder o bloco, deixe o array vazio: []
  cronograma: [
    { label: 'Inscrições', value: '10/08 a 18/08' },
    { label: 'Resultado', value: '28/08' },
    { label: 'Início das atividades', value: 'A definir' },
  ],

  /* Contato para dúvidas.
   * Enquanto `whatsapp` e `email` estiverem vazios, o bloco não aparece. */
  contato: {
    nome: 'Amanda Rosa',
    cargo: 'Presidente da Liga',
    whatsapp: '12981944548', // só números, com DDD
    email: 'amanda.rosa@sou.inteli.edu.br',
  },

  /* -------------------------------------------------------------------------
   *  RASCUNHO AUTOMÁTICO E CONTROLE DE REENVIO
   * -------------------------------------------------------------------------
   *  O formulário salva as respostas no navegador da pessoa enquanto ela
   *  preenche. Se ela atualizar ou fechar a página, tudo volta como estava.
   *  Depois de enviar, o rascunho é apagado e fica registrado que aquele
   *  navegador já concluiu a inscrição.
   *
   *  ⚠️  Trocar `storageKey` descarta TODOS os rascunhos e libera quem já
   *      enviou. Use isso se mudar as perguntas no meio do PS, ou no ano que
   *      vem (ex.: 'ati-ps-2027').
   * ---------------------------------------------------------------------- */
  storageKey: 'ati-ps-2026',

  /* Nome da tabela no Supabase que guarda as inscrições.
   * O script `npm run ps:sql` gera o SQL desta tabela a partir das perguntas
   * definidas mais abaixo. Se mudar aqui, rode o script de novo. */
  tabela: 'inscricoes_ps_2026',

  // Tela mostrada para quem já enviou a inscrição neste navegador
  jaEnviado: {
    title: 'Você já se inscreveu',
    message:
      'Encontramos uma inscrição já enviada por este navegador. Não é necessário preencher o formulário de novo — cada pessoa participa com uma única inscrição.',
    footnote:
      'Se você acha que houve algum engano ou precisa corrigir alguma resposta, fale com a presidência da Liga pelos contatos abaixo.',
  },

  // Tela exibida depois do envio
  success: {
    title: 'Inscrição recebida com sucesso!',
    message:
      'Muito obrigado por dedicar seu tempo para responder ao formulário. Agora nossa equipe vai analisar seu perfil para entender como podemos proporcionar a melhor experiência possível dentro da Liga AgroTech.',
    footnote:
      'Fique atento ao seu e-mail e ao WhatsApp — é por esses canais que divulgaremos as próximas etapas do processo. Nos vemos em breve!',
  },

  // Aviso de privacidade exibido no fim da última etapa
  privacyNotice:
    'Ao enviar, você concorda que a AgroTech Inteli utilize os dados informados exclusivamente para a condução deste processo seletivo.',
};

/* ---------------------------------------------------------------------------
 *  PERGUNTAS
 * ------------------------------------------------------------------------- */
export const psSections = [
  /* =========================================================================
   *  SEÇÃO 1 — INFORMAÇÕES GERAIS (todos respondem)
   * ======================================================================= */
  {
    id: 'informacoes_gerais',
    title: 'Informações gerais',
    description: 'Para conseguirmos falar com você e entender seu momento na faculdade.',
    questions: [
      {
        id: 'nome_completo',
        label: 'Nome completo',
        type: 'text',
        required: true,
        placeholder: 'Ex.: Maria Silva Santos',
        width: 'full',
      },
      {
        id: 'email',
        label: 'E-mail institucional',
        type: 'email',
        required: true,
        placeholder: 'seunome@sou.inteli.edu.br',
        help: 'É por ele que enviaremos as próximas etapas do processo.',
        width: 'half',
      },
      {
        id: 'whatsapp',
        label: 'WhatsApp',
        type: 'tel',
        required: true,
        placeholder: '(11) 91234-5678',
        width: 'half',
      },
      {
        id: 'curso',
        label: 'Curso',
        type: 'radio',
        required: true,
        options: [
          'Engenharia de Computação',
          'Engenharia de Software',
          'Sistemas de Informação',
          'Ciência da Computação',
          'ADM Tech',
        ],
        width: 'full',
      },
      {
        id: 'ano_ingresso',
        label: 'Ano de ingresso no Inteli',
        type: 'select',
        required: true,
        options: ['2023', '2024', '2025', '2026'],
        width: 'half',
      },
      {
        id: 'participa_entidade',
        label:
          'Você participa atualmente de alguma outra liga, entidade estudantil ou projeto extracurricular?',
        type: 'radio',
        required: true,
        options: [
          { value: 'nao', label: 'Não' },
          { value: 'sim', label: 'Sim' },
        ],
        width: 'full',
      },
      {
        id: 'participa_entidade_detalhe',
        label: 'Qual(is)? Qual seu papel e aproximadamente quantas horas semanais você dedica?',
        type: 'text',
        required: true,
        placeholder: 'Ex.: Empresa Júnior — coordenador de projetos, cerca de 6h por semana',
        showIf: { question: 'participa_entidade', equals: 'sim' },
        width: 'full',
      },
      {
        id: 'disponibilidade',
        label: 'Qual sua disponibilidade média para dedicar à Liga AgroTech?',
        type: 'radio',
        required: true,
        options: ['Até 2h por semana', 'Entre 2 e 4h', 'Entre 4 e 6h', 'Mais de 6h'],
        width: 'full',
      },
      {
        id: 'trilha',
        label: 'Qual trilha mais combina com você neste momento?',
        type: 'radio',
        required: true,
        help: 'Sua resposta define as próximas perguntas do formulário.',
        options: [
          { value: 'tech', label: 'Quero entrar na Trilha Tech (Desenvolvimento)' },
          { value: 'trainees', label: 'Quero entrar na Trilha Trainees (Negócios)' },
          { value: 'ambas', label: 'Ainda não sei / Tenho interesse nas duas' },
        ],
        width: 'full',
      },
    ],
  },

  /* =========================================================================
   *  SEÇÃO TECH — só para quem escolheu a Trilha Tech
   * ======================================================================= */
  {
    id: 'trilha_tech',
    title: 'Trilha Tech',
    description: 'Agora queremos conhecer melhor o seu perfil técnico.',
    intro: [
      'Não se preocupe caso você ainda esteja aprendendo. Valorizamos tanto quem já possui experiência quanto quem demonstra interesse e vontade de evoluir.',
      'Quanto mais detalhes você fornecer, melhor conseguiremos entender o seu perfil.',
    ],
    showIf: { question: 'trilha', equals: 'tech' },
    questions: [
      {
        id: 'tech_trajetoria',
        label: 'Conte um pouco sobre sua trajetória com tecnologia.',
        type: 'textarea',
        required: true,
        help: 'Pode incluir quando começou a estudar programação, disciplinas da faculdade, cursos, projetos pessoais, hackathons ou qualquer outra experiência.',
        placeholder: 'Comece por onde achar melhor...',
        maxLength: 2000,
        width: 'full',
      },
      {
        id: 'tech_tecnologias',
        label: 'Quais tecnologias você já conhece?',
        type: 'checkbox',
        required: true,
        help: 'Marque todas com as quais você tenha algum contato, mesmo que pouco.',
        columns: 3,
        options: [
          'Python',
          'Java',
          'C/C++',
          'JavaScript',
          'TypeScript',
          'HTML/CSS',
          'React',
          'Node',
          'SQL',
          'Git/GitHub',
          'Power BI',
          'IA/ML',
          'Cloud',
          'APIs',
          'Docker',
          'Outra',
        ],
        width: 'full',
      },
      {
        id: 'tech_tecnologias_detalhe',
        label: 'Para cada tecnologia marcada, descreva seu nível de conhecimento e como a utilizou.',
        type: 'textarea',
        required: true,
        placeholder: 'Ex.: Python — intermediário, usei em projetos de análise de dados...',
        maxLength: 2000,
        showIf: { question: 'tech_tecnologias', answered: true },
        width: 'full',
      },
      {
        id: 'tech_projetos',
        label: 'Conte sobre até três projetos dos quais você mais se orgulha.',
        type: 'textarea',
        required: true,
        help: 'Para cada projeto, descreva: objetivo, seu papel, tecnologias utilizadas, principais desafios e resultados obtidos.',
        maxLength: 3000,
        width: 'full',
      },
      {
        id: 'tech_links',
        label: 'Você possui portfólio, GitHub, GitLab e/ou LinkedIn?',
        type: 'text',
        required: true,
        help: 'Cole os links aqui. Se tiver mais de um, separe por vírgula.',
        placeholder: 'github.com/seu-usuario, linkedin.com/in/seu-perfil',
        maxLength: 500,
        width: 'full',
      },
      {
        id: 'tech_aprendizado',
        label:
          'Quando encontra um problema técnico que não sabe resolver, como costuma aprender e buscar soluções?',
        type: 'textarea',
        required: true,
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'tech_area_interesse',
        label: 'Qual área da tecnologia mais desperta seu interesse hoje?',
        type: 'textarea',
        required: true,
        help: 'Exemplos: IA, Ciência de Dados, Backend, Frontend, Full Stack, Cloud, IoT, Visão Computacional, AgroTech, entre outras.',
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'tech_quer_aprender',
        label:
          'Existe alguma tecnologia ou tema que você gostaria muito de aprender durante sua participação na Liga?',
        type: 'textarea',
        required: true,
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'tech_porque',
        label: 'Por que você acredita que a Trilha Tech da Liga AgroTech faz sentido para você?',
        type: 'textarea',
        required: true,
        help: 'O que espera construir ou aprender?',
        maxLength: 2000,
        width: 'full',
      },
    ],
  },

  /* =========================================================================
   *  SEÇÃO TRAINEES — só para quem escolheu a Trilha Trainees
   * ======================================================================= */
  {
    id: 'trilha_trainees',
    title: 'Trilha Trainees',
    description:
      'Agora queremos conhecer melhor o seu perfil voltado para negócios, gestão e desenvolvimento organizacional.',
    intro: [
      'Não é necessário já possuir experiência. Queremos entender seus interesses, habilidades e objetivos.',
    ],
    showIf: { question: 'trilha', equals: 'trainees' },
    questions: [
      {
        id: 'trainees_trajetoria',
        label:
          'Conte um pouco sobre sua trajetória em atividades de liderança, gestão ou organização.',
        type: 'textarea',
        required: true,
        help: 'Podem ser projetos, entidades estudantis, empresas juniores, voluntariado, eventos ou outras experiências.',
        maxLength: 2000,
        width: 'full',
      },
      {
        id: 'trainees_areas',
        label: 'Com quais áreas você possui mais afinidade?',
        type: 'checkbox',
        required: true,
        columns: 3,
        options: [
          'Marketing',
          'Eventos',
          'Comercial',
          'Parcerias',
          'Financeiro',
          'RH/Pessoas',
          'Projetos',
          'Estratégia',
          'Operações',
          'Produto',
          'Outro',
        ],
        width: 'full',
      },
      {
        id: 'trainees_areas_detalhe',
        label: 'Explique brevemente sua experiência ou interesse em cada área marcada.',
        type: 'textarea',
        required: true,
        maxLength: 2000,
        showIf: { question: 'trainees_areas', answered: true },
        width: 'full',
      },
      {
        id: 'trainees_projeto',
        label:
          'Conte sobre um projeto ou iniciativa em que você participou e que considera importante.',
        type: 'textarea',
        required: true,
        help: 'Descreva: contexto, seu papel, desafios e resultados.',
        maxLength: 3000,
        width: 'full',
      },
      {
        id: 'trainees_ferramentas',
        label: 'Quais ferramentas você já utilizou?',
        type: 'checkbox',
        required: true,
        columns: 3,
        options: [
          'Excel',
          'Power BI',
          'Canva',
          'Figma',
          'Notion',
          'Trello',
          'Pacote Office',
          'Google Workspace',
          'Jira',
          'Miro',
          'Slack',
          'Git/GitHub',
          'SQL',
          'Google Analytics',
          'CRM (HubSpot, Pipedrive...)',
          'Outra',
        ],
        width: 'full',
      },
      {
        id: 'trainees_ferramentas_detalhe',
        label: 'Explique como utilizou essas ferramentas.',
        type: 'textarea',
        required: true,
        maxLength: 2000,
        showIf: { question: 'trainees_ferramentas', answered: true },
        width: 'full',
      },
      {
        id: 'trainees_organizacao',
        label:
          'Como você costuma organizar suas atividades quando possui vários prazos ao mesmo tempo?',
        type: 'textarea',
        required: true,
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'trainees_area_interesse',
        label: 'Qual área de negócios mais desperta seu interesse atualmente e por quê?',
        type: 'textarea',
        required: true,
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'trainees_quer_aprender',
        label: 'O que você gostaria de aprender durante sua participação na Liga?',
        type: 'textarea',
        required: true,
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'trainees_porque',
        label: 'Por que você acredita que a Trilha Trainees faz sentido para você?',
        type: 'textarea',
        required: true,
        help: 'O que espera desenvolver?',
        maxLength: 2000,
        width: 'full',
      },
      {
        id: 'trainees_links',
        label: 'Adicione aqui seu portfólio, GitHub, GitLab e/ou LinkedIn.',
        type: 'text',
        required: true,
        help: 'Se tiver mais de um, separe por vírgula.',
        placeholder: 'linkedin.com/in/seu-perfil',
        maxLength: 500,
        width: 'full',
      },
    ],
  },

  /* =========================================================================
   *  SEÇÃO GERAL — para quem ainda não decidiu a trilha
   * ======================================================================= */
  {
    id: 'perfil_geral',
    title: 'Seu perfil',
    description: 'Sem problemas! Muitas pessoas ainda estão descobrindo qual área faz mais sentido para elas.',
    intro: [
      'Nesta etapa queremos entender o seu perfil de forma mais ampla, para identificar onde você poderá se desenvolver melhor.',
    ],
    showIf: { question: 'trilha', equals: 'ambas' },
    questions: [
      {
        id: 'geral_sobre_voce',
        label:
          'Conte um pouco sobre você, sua trajetória na faculdade e experiências que considera importantes.',
        type: 'textarea',
        required: true,
        maxLength: 2000,
        width: 'full',
      },
      {
        id: 'geral_atividades',
        label: 'Quais atividades você mais gosta de realizar?',
        type: 'textarea',
        required: true,
        help: 'Pode incluir tecnologia, negócios, organização, eventos, liderança, pesquisa, design, programação ou outras.',
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'geral_projeto',
        label: 'Conte sobre um projeto do qual você mais se orgulha.',
        type: 'textarea',
        required: true,
        help: 'Descreva seu papel, desafios e resultados.',
        maxLength: 2500,
        width: 'full',
      },
      {
        id: 'geral_ferramentas',
        label: 'Quais ferramentas, tecnologias ou softwares você já utilizou?',
        type: 'textarea',
        required: true,
        help: 'Explique como utilizou cada um.',
        maxLength: 2000,
        width: 'full',
      },
      {
        id: 'geral_pontos_fortes',
        label: 'Quais habilidades você acredita serem seus maiores pontos fortes?',
        type: 'textarea',
        required: true,
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'geral_desenvolver',
        label: 'Quais habilidades você gostaria de desenvolver durante sua participação na Liga?',
        type: 'textarea',
        required: true,
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'geral_projeto_ideal',
        label:
          'Se pudesse escolher um projeto para desenvolver dentro da Liga AgroTech, qual seria?',
        type: 'textarea',
        required: true,
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'geral_porque',
        label: 'Por que deseja entrar para a Liga AgroTech e o que espera levar dessa experiência?',
        type: 'textarea',
        required: true,
        maxLength: 2000,
        width: 'full',
      },
    ],
  },

  /* =========================================================================
   *  SEÇÃO FINAL — todos respondem
   * ======================================================================= */
  {
    id: 'final',
    title: 'Para finalizar',
    description: 'Quase lá — só mais uma pergunta e a confirmação.',
    questions: [
      {
        id: 'info_adicional',
        label:
          'Existe alguma informação que você gostaria de compartilhar e que não foi perguntada?',
        type: 'textarea',
        required: false,
        placeholder: 'Fique à vontade. Se não houver nada, é só deixar em branco.',
        maxLength: 1500,
        width: 'full',
      },
      {
        id: 'declaracao',
        label:
          'Declaro que as informações fornecidas neste formulário são verdadeiras e compreendo que este formulário possui caráter de mapeamento de perfil, não sendo eliminatório.',
        type: 'consent',
        required: true,
        width: 'full',
      },
    ],
  },
];

/* ---------------------------------------------------------------------------
 *  Lista achatada de todas as perguntas — útil para validação, para montar
 *  o payload do Supabase e para gerar o schema da tabela.
 * ------------------------------------------------------------------------- */
export const psQuestions = psSections.flatMap((section) =>
  section.questions.map((q) => ({ ...q, sectionId: section.id }))
);

/* ---------------------------------------------------------------------------
 *  Avalia uma condição `showIf` contra as respostas atuais.
 *  Usada tanto para perguntas quanto para seções inteiras.
 * ------------------------------------------------------------------------- */
export const isVisible = (showIf, answers = {}) => {
  if (!showIf) return true;
  const value = answers[showIf.question];

  if (showIf.answered !== undefined) {
    const hasValue = Array.isArray(value)
      ? value.length > 0
      : value !== undefined && value !== null && String(value).trim() !== '';
    return showIf.answered ? hasValue : !hasValue;
  }
  if (showIf.in) return showIf.in.includes(value);
  if (showIf.equals !== undefined) return value === showIf.equals;
  return true;
};
